import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiUsers } from 'react-icons/fi';
import ClientForm from '../components/shared/ClientForm';
import { appendLicenseFields, toYmd } from '../components/shared/clientFormConfig';
import { Badge, EmptyState, PageHeader, Skeleton, toast } from '../components/ui';
import { usePageCrumbs } from '../components/shell';
import {
  downloadFile,
  editEntreprise,
  showEntreprise,
} from '../servicesV2/entrepriseService';
import { getWhatsAppUsage, whatsAppBudget, whatsAppCost } from '../servicesV2/whatsappService';

function EditClient() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [client, setClient] = useState(null);
  const [initialLogoUrl, setInitialLogoUrl] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [whatsApp, setWhatsApp] = useState(null);

  usePageCrumbs(
    client
      ? [{ label: client.Nom, to: `/edit-client/${clientId}` }, { label: 'Modifier' }]
      : [],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await showEntreprise(clientId);
        if (cancelled) return;
        setClient(res);
        // TODO: re-enable once /public/download is fixed for the "master" pseudo-tenant.
        // Currently returns 401 ("Invalid tenant") which trips the global logout interceptor.
        // if (res.Logo) {
        //   try {
        //     const url = await downloadFile(res.Logo);
        //     if (!cancelled) setInitialLogoUrl(url);
        //   } catch (err) {
        //     console.error('Logo fetch failed:', err);
        //   }
        // }
      } catch (err) {
        console.error(err);
        toast.error('Chargement impossible', { description: err?.message });
      } finally {
        if (!cancelled) setFetching(false);
      }
    }
    load();
    // Secondary: the form must open even if the counter fails.
    getWhatsAppUsage().then(
      (usage) => !cancelled && setWhatsApp(usage),
      () => {},
    );
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleSubmit = async ({ data, modules, keptModules, logo }) => {
    setLoading(true);
    setErrorText('');
    try {
      const formData = new FormData();
      formData.append('Activite', data.Activite);
      formData.append('DomainApplication', data.DomainApplication);
      formData.append('JustificationExclusion', data.JustificationExclusion || '');
      formData.append('Key', data.Key || client?.Key || '');
      formData.append('Nom', data.Nom);
      formData.append('RaisonSocial', data.RaisonSocial);
      // Plain YYYY-MM-DD: toISOString() shifted the day back in UTC+1, and the
      // licence signs exactly these dates.
      formData.append('endDate', toYmd(data.endDate));
      formData.append('representantEmail', data.representantEmail);
      formData.append('representantName', data.representantName);
      formData.append('startDate', toYmd(data.startDate));
      formData.append('isOnPremise', data.isOnPremise ? 'true' : 'false');
      if (data.isOnPremise) formData.append('publicApiUrl', data.publicApiUrl || '');
      // « 0,05 » typed the French way is sent as 0.05.
      formData.append('whatsappMonthlyBudgetUsd', String(data.whatsappMonthlyBudgetUsd ?? '').trim().replace(',', '.'));
      // E7 AI threshold: same decimal handling; empty string = no threshold.
      formData.append('aiMonthlyBudgetUsd', String(data.aiMonthlyBudgetUsd ?? '').trim().replace(',', '.'));
      // SWOT lot 3b: web search switch (off for on-premise, as the server also enforces) and the searched name.
      formData.append('webSearchEnabled', !data.isOnPremise && data.webSearchEnabled ? 'true' : 'false');
      formData.append('webSearchNewsName', String(data.webSearchNewsName ?? '').trim());
      if (logo) formData.append('PieceJointe', logo);
      modules.forEach((m) => formData.append('modules[]', m));
      appendLicenseFields(formData, data, keptModules);

      await editEntreprise(formData, clientId);
      toast.success('Client mis à jour', { description: data.Nom });
      navigate('/clients');
    } catch (err) {
      const serverKey = err?.response?.data?.message;
      const message = serverKey
        ? t(`ERRORS.${serverKey}`, { defaultValue: serverKey })
        : t('ERRORS.ERROR', { defaultValue: 'Une erreur est survenue.' });
      setErrorText(message);
      toast.error('Échec de la mise à jour', { description: message });
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <>
        <PageHeader title="Chargement…" />
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <Skeleton className="hidden h-60 rounded-md lg:block" />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-60 rounded-lg" />
          </div>
        </div>
      </>
    );
  }

  if (!client) {
    return (
      <>
        <PageHeader title="Client introuvable" />
        <EmptyState
          variant="danger"
          icon={<FiUsers size={22} />}
          title="Impossible de charger ce client"
          description="Il a peut-être été supprimé ou l'URL est incorrecte."
        />
      </>
    );
  }

  const whatsAppUsage = whatsApp?.tenants.find((tenant) => tenant.entrepriseId === client.id);

  const summary = (
    <div className="rounded-lg border border-line bg-surface shadow-e1 p-3">
      <p className="text-micro text-fg-subtle">Client</p>
      <p className="mt-1 truncate text-body-sm font-semibold text-fg">{client.Nom}</p>
      <div className="mt-1 flex items-center gap-1.5">
        <Badge variant="mono" size="sm">
          {client.Key}
        </Badge>
      </div>
      {whatsAppUsage && (
        <div className="mt-3 border-t border-line pt-2">
          <p className="text-micro text-fg-subtle">Messages WhatsApp envoyés</p>
          {whatsAppUsage.current == null ? (
            <p className="mt-1 text-caption text-fg-muted">Compteur indisponible</p>
          ) : (
            <>
              <p className="mt-1 text-body-sm text-fg">
                <span className="tabular-nums font-semibold">{whatsAppUsage.current}</span> en{' '}
                {whatsApp.currentMonth} (~{whatsAppCost(whatsAppUsage.current, whatsApp)} $ estimés)
              </p>
              <p className="text-caption text-fg-muted">
                Budget du mois : {whatsAppBudget(whatsAppUsage, whatsApp).budget} $
                {whatsAppBudget(whatsAppUsage, whatsApp).reached && ' — atteint'}
              </p>
              <p className="text-caption text-fg-muted">
                <span className="tabular-nums">{whatsAppUsage.previous}</span> en {whatsApp.previousMonth}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Modifier le client"
        description={`Édition de ${client.Nom}`}
      />
      <ClientForm
        mode="edit"
        initialValues={{
          Nom: client.Nom || '',
          Key: client.Key || '',
          representantName: client.representantName || '',
          representantEmail: client.representantEmail || '',
          RaisonSocial: client.RaisonSocial || '',
          Activite: client.Activite || '',
          startDate: client.startDate,
          endDate: client.endDate,
          DomainApplication: client.DomainApplication || '',
          JustificationExclusion: client.JustificationExclusion || '',
          isOnPremise: client.isOnPremise ?? false,
          publicApiUrl: client.publicApiUrl || '',
          whatsappMonthlyBudgetUsd:
            client.whatsappMonthlyBudgetUsd == null ? '' : String(Number(client.whatsappMonthlyBudgetUsd)),
          aiMonthlyBudgetUsd:
            client.aiMonthlyBudgetUsd == null ? '' : String(Number(client.aiMonthlyBudgetUsd)),
          webSearchEnabled: client.webSearchEnabled ?? true,
          webSearchNewsName: client.webSearchNewsName || '',
          modules: Array.isArray(client.modules) ? client.modules : [],
          // No stored signature = unlimited today: keep it so unless the admin
          // turns the switch off, so saving a logo never locks a client.
          licenseUnlimited: !client.licenseSignature,
          licenseExpiryMode: client.licenseExpiryMode || 'full',
          licenseKeptModules: client.licenseKeptModules ? client.licenseKeptModules.split(',') : [],
          license: client.license,
        }}
        initialLogoUrl={initialLogoUrl}
        disabledFields={['Key']}
        showColor={false}
        submitLabel="Enregistrer les modifications"
        onSubmit={handleSubmit}
        onCancel={() => navigate('/clients')}
        errorText={errorText}
        loading={loading}
        summary={summary}
      />
    </>
  );
}

export default EditClient;
